import { Component, OnInit, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { DealerRevenueService, DealerRevenueRecord, DealerRevenueUpsert } from '@app/_services/dealer-revenue.service';
import { AgGridModule } from 'ag-grid-angular';
import { ColDef, GridApi, GridReadyEvent } from 'ag-grid-community';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-dealer-revenue',
  standalone: true,
  templateUrl: './dealer-revenue.component.html',
  styleUrls: ['./dealer-revenue.component.less'],
  imports: [CommonModule, ReactiveFormsModule, AgGridModule]
})
export class DealerRevenueComponent implements OnInit, OnDestroy {
  searchForm: FormGroup;
  rowData: DealerRevenueRecord[] = [];
  loading = false;
  gridApi!: GridApi;
  showFilters = true;
  upsertMode = false;
  upsertForm!: FormGroup;
  selectedRecord?: DealerRevenueRecord;
  private subscriptions: Subscription[] = [];

  monthNames = [
    { field: '01', headerName: 'Jan' },
    { field: '02', headerName: 'Feb' },
    { field: '03', headerName: 'Mar' },
    { field: '04', headerName: 'Apr' },
    { field: '05', headerName: 'May' },
    { field: '06', headerName: 'Jun' },
    { field: '07', headerName: 'Jul' },
    { field: '08', headerName: 'Aug' },
    { field: '09', headerName: 'Sep' },
    { field: '10', headerName: 'Oct' },
    { field: '11', headerName: 'Nov' },
    { field: '12', headerName: 'Dec' }
  ];

  columnDefs: ColDef[] = [
    { field: 'dealerId', headerName: 'Dealer ID', flex: 1, minWidth: 120 },
    { field: 'year', headerName: 'Year', flex: 0.7, minWidth: 90 },
    { field: 'dealerCity', headerName: 'City', flex: 1, minWidth: 120 },
    { field: 'dealerState', headerName: 'State', flex: 1, minWidth: 120 },
    // Month columns
    ...this.monthNames.map(m => ({
      headerName: m.headerName,
      minWidth: 100,
      valueGetter: (params: any) => {
        try {
          if (!params.data || !params.data.revenueAmount || !Array.isArray(params.data.revenueAmount)) {
            return '';
          }
          const entry = params.data.revenueAmount.find((r: any) => r.month === m.field);
          return entry ? `₹${entry.revenue}${entry.revenueType ? ' (' + entry.revenueType + ')' : ''}` : '';
        } catch (error) {
          console.warn('Error in valueGetter for month', m.field, error);
          return '';
        }
      }
    })),
    {
      headerName: 'Actions',
      cellRenderer: (params: any) => `<button class='btn btn-sm btn-primary' data-action='upsert'>Add/Update</button>`,
      minWidth: 120,
      flex: 0.7
    }
  ];
  defaultColDef: ColDef = { 
    resizable: true, 
    filter: true, 
    sortable: true,
    suppressSizeToFit: false
  };

  constructor(private fb: FormBuilder, private dealerRevenueService: DealerRevenueService) {
    const currentYear = new Date().getFullYear();
    this.searchForm = this.fb.group({
      dealerId: [''],
      year: [currentYear, [Validators.required, Validators.min(2000)]],
      dealerCity: [''],
      dealerState: ['']
    });
  }

  ngOnInit(): void {
    this.onSearch();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
    window.removeEventListener('resize', this.onWindowResize);
  }

  toggleFilters(): void {
    this.showFilters = !this.showFilters;
  }

  onGridReady(params: GridReadyEvent): void {
    this.gridApi = params.api;
    window.addEventListener('resize', this.onWindowResize);
    
    // Ensure the grid is properly sized
    setTimeout(() => {
      if (this.gridApi) {
        this.gridApi.sizeColumnsToFit();
        // Force refresh to ensure data is displayed
        if (this.rowData && this.rowData.length > 0) {
          this.gridApi.setRowData(this.rowData);
        }
      }
    }, 200);
  }

  private onWindowResize = () => {
    if (this.gridApi) {
      setTimeout(() => this.gridApi.sizeColumnsToFit(), 100);
    }
  };

  onSearch(): void {
    if (this.searchForm.invalid) return;
    this.loading = true;
    const sub = this.dealerRevenueService.search(this.searchForm.value).subscribe({
      next: data => {
        console.log('Search results:', data);
        console.log('Data type:', typeof data, 'Is array:', Array.isArray(data));
        if (data && data.length > 0) {
          console.log('First record structure:', JSON.stringify(data[0], null, 2));
        }
        this.rowData = data || [];
        this.loading = false;
        
        // Ensure grid updates properly
        setTimeout(() => {
          if (this.gridApi) {
            this.gridApi.setRowData(this.rowData);
            this.gridApi.sizeColumnsToFit();
          }
        }, 100);
      },
      error: () => {
        this.rowData = [];
        this.loading = false;
        if (this.gridApi) {
          this.gridApi.setRowData([]);
        }
      }
    });
    this.subscriptions.push(sub);
  }

  onGridCellClicked(event: any): void {
    if (event.colDef.headerName === 'Actions' && event.event.target?.dataset.action === 'upsert') {
      this.selectedRecord = event.data;
      this.openUpsertForm(event.data);
    }
  }

  openUpsertForm(record?: DealerRevenueRecord): void {
    this.upsertMode = true;
    this.upsertForm = this.fb.group({
      dealerId: [record?.dealerId || '', Validators.required],
      year: [record?.year || new Date().getFullYear(), [Validators.required, Validators.min(2000)]],
      dealerCity: [record?.dealerCity || ''],
      dealerState: [record?.dealerState || ''],
      month: ['', [Validators.required, Validators.pattern('^(0[1-9]|1[0-2])$')]],
      revenue: [null, [Validators.required, Validators.min(0)]],
      revenueType: ['IGST', [Validators.required]]
    });
  }

  closeUpsertForm(): void {
    this.upsertMode = false;
    this.selectedRecord = undefined;
  }

  submitUpsert(): void {
    if (this.upsertForm.invalid) return;
    this.loading = true;
    const sub = this.dealerRevenueService.upsert(this.upsertForm.value as DealerRevenueUpsert).subscribe({
      next: () => {
        this.loading = false;
        this.closeUpsertForm();
        this.onSearch();
      },
      error: () => {
        this.loading = false;
      }
    });
    this.subscriptions.push(sub);
  }
}
